import { produce } from 'immer';
import { DraftDesignConfiguration, DraftState } from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { getImageAndType } from '@/utils/image/getImageAndType';
import { getDesignImageUrl } from '@/utils/imageUrls/getDesignImageUrl';
import { getConfigurationMaskObjectUrl } from '@/utils/masks/getConfigurationMaskObjectUrl';
import { getMaskedDesignPreview } from '@/utils/masks/getMaskedDesignPreview';
import { getImageObjectUrl } from '@/utils/upload/imageObjectUrls';
import { generateAndStoreFilterImage } from '@/features/filters/filters';
import { getConfigurationFilterObjectUrl } from '@/features/filters/getConfigurationFilterObjectUrl';
import { isDraftDesignConfiguration } from '@/model/configuration/isDraftConfigurationType';
import { assertPixelDesign } from '@/model/design/isDesignType';
import { getConfiguration } from '@/model/draft/getConfiguration';

export const prepareProcessedImages = async (currentDraft: DraftState, draft: DraftState) => {
  const processedImages: Map<number, string> = new Map();

  await Promise.all(
    draft.configurationIds.map(async (configurationId) => {
      const nextConfiguration = getConfiguration(draft, configurationId);
      if (isDraftDesignConfiguration(nextConfiguration)) {
        const { design, processedImage, filter, mask } = nextConfiguration;
        const { filter: currentFilter, mask: currentMask } = (getConfiguration(
          currentDraft,
          configurationId
        ) || {}) as DraftDesignConfiguration;
        if (
          (filter &&
            (filter !== currentFilter || // filter did change
              (!mask && processedImage !== getConfigurationFilterObjectUrl(configurationId)))) || // filter was removed
          (mask &&
            (mask !== currentMask || // mask did change
              processedImage !== getConfigurationMaskObjectUrl(configurationId))) // mask was removed
        ) {
          if (filter) {
            const [designImage, designImageType] = await getImageAndType(
              getImageObjectUrl(design.id) || getDesignImageUrl(design.id, { width: 600 })
            );
            await generateAndStoreFilterImage(
              designImage,
              designImageType,
              configurationId,
              filter.settings
            );
          }

          let processedImage: string;
          if (mask) {
            await getMaskedDesignPreview(
              mask,
              configurationId,
              assertPixelDesign(design),
              mask.scale,
              mask.offset,
              !filter
            );

            processedImage = assertDefined(getConfigurationMaskObjectUrl(configurationId));
          } else {
            // create the filter image before the mask but return it after in case the config has both
            processedImage = assertDefined(getConfigurationFilterObjectUrl(configurationId));
          }

          processedImages.set(configurationId, processedImage);
        }
      }
    })
  );

  if (processedImages.size) {
    return produce(draft, (draftState) => {
      processedImages.forEach((processedImage, configurationId) => {
        const configuration = getConfiguration(draftState, configurationId);
        if (isDraftDesignConfiguration(configuration)) {
          configuration.processedImage = processedImage;
        }
      });
    });
  }

  return draft;
};
